/**
 * @fileoverview Edit User placement fields.
 * @description The Edit dialog's role and reporting-manager pickers, fed by the edit options (the
 * data and the role-change handling are in useEditUserPlacement). Changing the role clears the
 * manager, and the managers re-load for the chosen role.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import UserRequestError from '@/features/users/components/UserRequestError'
import { useEditUserPlacement } from '@/features/users/hooks/useEditUserPlacement'
import SelectField from '@/shared/components/molecules/SelectField'

import type { EditUserFormValues } from '@/features/users/schemas/edit-user-form.schema'
import type { UseFormReturn } from 'react-hook-form'

/**
 * @component
 * @param {{ form: UseFormReturn<EditUserFormValues>; userId: string }} props - The form and the
 * user being edited.
 * @param {UseFormReturn<EditUserFormValues>} props.form - The edit form.
 * @param {string} props.userId - The user being edited.
 */
function EditUserPlacementFields({
  form,
  userId,
}: {
  form: UseFormReturn<EditUserFormValues>
  userId: string
}) {
  const { t } = useTranslation()
  const placement = useEditUserPlacement(form, userId)
  const placeholder = t('sales.userManagement.editUserForm.fields.reportsToPlaceholder')

  return (
    <>
      <SelectField
        id="editUserRole"
        data-testid="edit-user-role-select"
        label={`${t('sales.userManagement.editUserForm.fields.role')} *`}
        options={[
          { value: '', label: placeholder },
          ...placement.roles.map((role) => ({ value: role.roleId, label: role.name })),
        ]}
        error={placement.roleError}
        {...form.register('primaryRoleId', { onChange: placement.onRoleChange })}
      />
      <div>
        <SelectField
          id="editUserReportingManager"
          data-testid="edit-user-reporting-manager-select"
          label={`${t('sales.userManagement.editUserForm.fields.reportsTo')} *`}
          options={[
            { value: '', label: placeholder },
            ...placement.managers.map((manager) => ({
              value: manager.userId,
              label: manager.displayName,
            })),
          ]}
          error={placement.managerError}
          {...form.register('reportingManagerUserId')}
        />
        {placement.noManagersAvailable && (
          <p className="mt-1.5 text-[12px] text-slate" data-testid="edit-user-no-managers-message">
            {t('sales.userManagement.editUserForm.fields.reportsToNoneAvailable')}
          </p>
        )}
      </div>
      {placement.optionsError && (
        <UserRequestError error={placement.optionsError} testIdPrefix="edit-user" />
      )}
    </>
  )
}

export default EditUserPlacementFields
